'use client'

import { forwardRef, useEffect, useState } from 'react'
import clsx from 'clsx'

import TextField, { TextFieldProps } from 'shared/ui/form/TextField'
import { shouldPreventKeyForNumberInput } from 'shared/utils/form'

import styles from './ProductQuantityInput.module.scss'

interface StyledProductQuantityInputProps
  extends Omit<TextFieldProps, 'onChange'> {
  value: number
  onChange: (evalue: number) => void
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  quantityLimit: number
  disabled?: boolean
}

const ProductQuantityInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  StyledProductQuantityInputProps
>(function ProductQuantityInputInner(
  {
    value,
    onChange,
    onKeyDown,
    quantityLimit,
    disabled,
    ...props
  }: StyledProductQuantityInputProps,
  ref
) {
  const [inputValue, setInputValue] = useState<number | ''>(value)

  // Sync inputValue with value prop when it changes
  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    shouldPreventKeyForNumberInput({
      type: 'number',
      key: event.key,
      additionalBlockedKeys: ['.', ',']
    }) && event.preventDefault()
    onKeyDown?.(event)
  }

  const onCustomChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value

    if (newValue === '') return setInputValue('')

    const numericValue = Number(newValue)

    const isInteger = numericValue % 1 === 0

    if (numericValue < 1 || !isInteger) return

    const valueToChange =
      numericValue > quantityLimit ? quantityLimit : numericValue

    setInputValue(valueToChange)
    onChange(valueToChange)
  }

  const handleBlur = () => {
    if (inputValue === '') {
      setInputValue(1)
      onChange(1)
    }
  }
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    event.target instanceof HTMLInputElement && event.target.blur()
  }

  return (
    <StyledProductQuantityInput
      value={inputValue}
      onBlur={handleBlur}
      onChange={onCustomChange}
      disabled={disabled}
      type='number'
      ref={ref}
      onKeyDown={e => handleKeyDown(e)}
      size='medium'
      {...props}
      inputProps={{
        max: quantityLimit,
        min: 1,
        inputMode: 'numeric',
        autoComplete: 'off',
        onWheel: handleWheel,
        ...props?.inputProps
      }}
    />
  )
})

export default ProductQuantityInput

export const StyledProductQuantityInput = ({
  className,
  ...props
}: TextFieldProps) => (
  <TextField className={clsx(styles.quantityInput, className)} {...props} />
)
